// CursosFichasSection.js — Fichas de curso. Dos formatos:
//  • 'docentes' (original): una ficha por documento con foto + ponencia + avance.
//  • 'modulos': grilla de tarjetas por módulo (intro+docente, pill "Módulo N",
//    sesiones=lecciones con docente+bandera) y un botón general abajo.
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { BACKEND_BASE, mentor2Service } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { conI18n } from '../../../utils/i18nTexto';
import { useIdioma } from '../../../contexts/IdiomaContext';
import { buildBg } from '../bgHelpers';
import { buscarCodigoPais } from '../../../utils/countryCodes';

const Wrapper = styled.section`
  padding: ${p => p.$padding || '3rem 0'};
  background: ${p => p.$bg || '#0b0218'};
  /* Foto de fondo opcional; el velo la atenúa para que las tarjetas se lean. */
  ${p => p.$bgImage && `
    background-image: ${p.$overlay ? `linear-gradient(${p.$overlay}, ${p.$overlay}), ` : ''}url(${p.$bgImage});
    background-size: ${p.$bgSize || 'cover'};
    background-position: ${p.$bgPos || 'center'};
    background-repeat: no-repeat;
  `}
`;

const Header = styled.div`
  max-width: 1080px;
  margin: 0 auto 1.5rem;
  padding: 0 1.5rem;
  text-align: ${p => p.$align || 'left'};
`;

const Title = styled.h2`
  font-size: ${p => p.$size || '32px'};
  color: ${p => p.$color || '#fff'};
  font-family: inherit;
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled.div`
  font-size: ${p => p.$size || '1rem'};
  color: ${p => p.$color || 'rgba(255,255,255,0.7)'};
  margin: 0.5rem 0 0;
`;

// ===== Formato original (docentes) =====
const List = styled.div`
  max-width: 1080px; margin: 0 auto; padding: 0 1.5rem;
  display: flex; flex-direction: column; gap: 1.5rem;
`;
const Card = styled.div`
  background: ${p => p.$bg || '#1B0B30'}; border-radius: 22px; padding: 2rem 2.5rem;
  display: flex; align-items: center; gap: 2rem;
  @media (max-width: 768px) { flex-direction: column; text-align: center; padding: 1.5rem; gap: 1rem; }
`;
const FotoWrap = styled.div`position: relative; flex-shrink: 0;`;
const Foto = styled.div`
  width: 160px; height: 160px; border-radius: 50%;
  background-image: url(${p => p.$src}); background-size: cover; background-position: center;
  background-color: rgba(255,255,255,0.1);
  @media (max-width: 768px) { width: 120px; height: 120px; }
`;
const Flag = styled.img`
  position: absolute; top: 4px; right: 4px; width: 40px; height: 40px; border-radius: 50%;
  object-fit: cover; border: 3px solid #fff; background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.35);
`;
const Body = styled.div`flex: 1; min-width: 0;`;
const Nombre = styled.h3`margin: 0 0 0.4rem; font-size: ${p => p.$size || '1.25rem'}; font-weight: 700; color: ${p => p.$color || '#FF00FF'};`;
const Texto = styled.p`margin: 0 0 0.4rem; font-size: ${p => p.$size || '1rem'}; line-height: 1.5; color: rgba(255,255,255,0.85);`;
const PonenciaLabel = styled.div`margin: 0.8rem 0 0.2rem; font-size: ${p => p.$size || 'inherit'}; font-weight: 700; color: ${p => p.$color || '#FF00FF'};`;
const Footer = styled.div`
  margin-top: 1.2rem; display: flex; flex-direction: column; align-items: flex-end;
  @media (max-width: 768px) { align-items: center; }
`;
const Barra = styled.div`width: 320px; max-width: 100%; height: 4px; border-radius: 4px; background: rgba(255,255,255,0.2); overflow: hidden;`;
const BarraFill = styled.div`height: 100%; width: ${p => p.$pct || 0}%; background: ${p => p.$color || '#FF00FF'};`;
const Pct = styled.div`font-size: ${p => p.$size || '0.78rem'}; font-weight: 700; letter-spacing: 0.5px; color: #fff; margin: 0.4rem 0;`;
const BtnRow = styled.div`
  display: flex; gap: 0.6rem; flex-wrap: wrap; justify-content: flex-end; margin-top: 0.2rem;
  @media (max-width: 768px) { justify-content: center; }
`;
const Btn = styled.button`
  background: ${p => p.$bg || '#7c3aed'};
  color: ${p => p.$color || '#fff'};
  border: ${p => p.$border || 'none'};
  border-radius: ${p => p.$radius || '10px'};
  padding: ${p => p.$padding || '0.6rem 1.8rem'}; font-size: ${p => p.$size || '0.95rem'};
  font-weight: ${p => p.$peso || 600}; font-family: inherit; cursor: pointer;
  transition: 0.2s; &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  /* Examen todavía bloqueado: el botón se ve, pero apagado y sin acción. */
  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
`;
// Parpadeo/resplandor para el botón de evaluación pendiente
const parpadeoEval = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 transparent; transform: translateY(0); }
  50% { box-shadow: 0 0 16px 3px currentColor; transform: translateY(-1px); }
`;
const BtnAlt = styled(Btn)`
  background: transparent; border: 1.5px solid ${p => p.$color || 'rgba(255,255,255,0.5)'}; color: ${p => p.$color || '#fff'};
  ${p => p.$glow && css`animation: ${parpadeoEval} 1.2s ease-in-out infinite;`}
`;
// Leyenda "Pendiente evaluación"
const PendienteEval = styled.div`
  font-size: ${p => p.$size || '0.72rem'}; font-weight: 700; letter-spacing: 0.4px;
  color: ${p => p.$color || '#f59e0b'}; text-align: center; margin: 0.15rem 0 0.5rem;
`;
// Lista de varios docentes seleccionados dentro de una ficha de documento
const DocentesLista = styled.div`display: flex; flex-direction: column; gap: 1.2rem; width: 100%;`;
const DocenteItem = styled.div`
  display: flex; align-items: center; gap: 1.1rem;
  @media (max-width: 768px) { flex-direction: column; text-align: center; }
`;
const MiniFotoWrap = styled.div`position: relative; flex-shrink: 0;`;
const MiniFoto = styled.div`
  width: ${p => p.$size || '92px'}; height: ${p => p.$size || '92px'}; border-radius: 50%;
  background-image: url(${p => p.$src}); background-size: cover; background-position: center;
  background-color: rgba(255,255,255,0.1);
`;
const MiniFlag = styled.img`
  position: absolute; bottom: 0; right: 0; width: 26px; height: 26px; border-radius: 50%;
  object-fit: cover; border: 2px solid #fff; background: #fff;
`;

// ===== Formato 'modulos' (grilla de bloques) =====
const ModGrid = styled.div`
  max-width: ${p => p.$maxWidth || '1080px'}; margin: 0 auto; padding: 0 1.5rem;
  display: grid; grid-template-columns: repeat(${p => p.$cols || 2}, 1fr); gap: ${p => p.$gap || '1.5rem'};
  ${p => p.$igualAltura ? 'grid-auto-rows: 1fr;' : ''}
  @media (max-width: 768px) { grid-template-columns: 1fr; ${p => p.$igualAltura ? 'grid-auto-rows: auto;' : ''} }
`;
// Celda de un curso dentro de la grilla de columnas (formato 'cursos_separados'):
// apila verticalmente sus módulos.
const CursoCol = styled.div`
  display: flex; flex-direction: column; gap: ${p => p.$gap || '1.5rem'};
  ${p => p.$fill ? 'height: 100%;' : ''}
`;
const CursoTitulo = styled.h3`
  font-size: ${p => p.$size || '20px'}; color: ${p => p.$color || '#0f355b'};
  font-family: inherit; font-weight: 700; margin: 0;
  text-align: ${p => p.$align || 'left'};
`;
const ModCard = styled.div`
  background: ${p => p.$bg || '#ffffff'};
  border: 1px solid ${p => p.$border || 'rgba(15,53,91,0.08)'};
  border-radius: ${p => p.$radius || '16px'}; padding: 1.5rem 1.6rem;
  /* La sombra se puede apagar («none») cuando el diseño quiere la portada
     suelta sobre el fondo, sin caja. */
  box-shadow: ${p => p.$sombra || '0 6px 20px rgba(15,53,91,0.06)'};
  ${p => p.$fill ? 'flex: 1; height: 100%;' : ''}
  /* Con imagen de portada la tarjeta no lleva padding propio: la imagen va a
     sangre y el texto se separa dentro de ModBody. */
  ${p => p.$noPad ? `padding: 0; overflow: ${p.$sinRecorte ? 'visible' : 'hidden'}; display: flex; flex-direction: column;` : ''}
`;
/* Barra de avance dentro de la tarjeta de módulo (catálogo claro). */
const ModBarra = styled.div`
  width: 100%;
  height: ${p => p.$alto || '5px'};
  border-radius: 999px;
  background: ${p => p.$bg || '#e2e8f0'};
  overflow: hidden;
`;
const ModBarraFill = styled.div`
  height: 100%;
  width: ${p => p.$pct || 0}%;
  background: ${p => p.$color || '#0f355b'};
  transition: width 0.3s;
`;
const ModPct = styled.div`
  margin-top: 0.4rem;
  text-align: center;
  font-size: ${p => p.$size || '0.72rem'};
  font-weight: 700;
  color: ${p => p.$color || '#0f355b'};
`;

// ===== Formato 'modulos_separados' (tarjeta de catálogo con portada) =====
// Portada a sangre arriba de la tarjeta.
const ModImg = styled.div`
  width: 100%; height: ${p => p.$h || '170px'}; flex-shrink: 0;
  ${p => p.$radio ? `border-radius: ${p.$radio};` : ''}
  background-image: url(${p => p.$src}); background-size: cover; background-position: center;
  background-color: #e2e8f0;
  /* Con el estado o el título encima, la portada es el lienzo de los dos. */
  ${p => p.$conEncima ? `position: relative; display: flex; flex-direction: column; justify-content: ${p.$cintaFuera ? 'flex-end' : 'space-between'};` : ''}
`;

/* Estado del módulo en una esquina de la portada («Sin iniciar», «En curso»,
   «Completado»). Sale del mismo avance que la barra, así que no hay que
   mantenerlo a mano. */
const ModEstado = styled.span`
  align-self: flex-start;
  position: relative;
  background: ${p => p.$bg || '#2f80ed'};
  color: ${p => p.$color || '#ffffff'};
  font-size: ${p => p.$size || '13px'};
  font-weight: ${p => p.$peso || 500};
  padding: ${p => p.$padding || '4px 12px'};
  border-radius: ${p => p.$radio || '0'};
  white-space: nowrap;
  /* Estilo cinta: la misma banderita del catálogo de LearnDash. Va absoluta y
     asomando por el borde izquierdo, y el dobladillo se dibuja con los bordes
     del ::before (arriba y derecha en el tono oscuro, el resto transparente),
     que es lo que le da la muesca doblada bajo el saliente. */
  ${p => p.$cinta && `
    position: absolute;
    left: -${p.$saliente || '8px'};
    top: ${p.$separacion || '10px'};
    z-index: 2;
    box-shadow: 0 1px rgba(0, 0, 0, 0.2);
    text-shadow: 0 1px rgba(0, 0, 0, 0.3);
    &::before {
      content: '';
      position: absolute;
      left: 0;
      bottom: -${p.$pliegueAlto || '8px'};
      border: ${p.$pliegueGrosor || '4px'} solid transparent;
      border-top: ${p.$pliegueGrosor || '4px'} solid ${p.$pliegue || 'rgba(0,0,0,0.35)'};
      border-right: ${p.$pliegueGrosor || '4px'} solid ${p.$pliegue || 'rgba(0,0,0,0.35)'};
    }
  `}
`;

/* Título del módulo sobre una franja al pie de la portada. */
const ModTituloSobre = styled.div`
  width: 100%;
  ${p => p.$radio ? `border-radius: 0 0 ${p.$radio} ${p.$radio};` : ''}
  background: ${p => p.$bg || 'rgba(15,23,42,0.72)'};
  color: ${p => p.$color || '#ffffff'};
  font-size: ${p => p.$size || '17px'};
  line-height: ${p => p.$lineHeight || '1.3'};
  text-align: ${p => p.$align || 'center'};
  padding: ${p => p.$padding || '12px 14px'};
  box-sizing: border-box;
  transition: background 0.25s ease, transform 0.25s ease, padding 0.25s ease;
  /* Al pasar por la tarjeta, la franja puede cambiar de color, crecer (con más
     aire arriba y abajo) o levantarse. Sin esas claves no se mueve nada, así
     que las secciones ya publicadas siguen igual. */
  ${ModCard}:hover & {
    ${p => p.$bgHover ? `background: ${p.$bgHover};` : ''}
    ${p => p.$paddingHover ? `padding: ${p.$paddingHover};` : ''}
    ${p => p.$subida ? `transform: translateY(-${p.$subida});` : ''}
  }
`;
// Cuerpo de la tarjeta de catálogo (debajo de la portada).
const ModBody = styled.div`
  padding: ${p => p.$padding || '1.6rem 1.7rem'};
  display: flex; flex-direction: column;
  ${p => p.$align === 'center' ? 'align-items: center; text-align: center;' : ''}
  ${p => p.$fill ? 'flex: 1;' : ''}
`;
// Título real del módulo (mentor_modulos.titulo), no el título ★ personalizado.
const ModTitulo = styled.h3`
  margin: 0; font-family: inherit; font-weight: 700;
  line-height: ${p => p.$lineHeight || '1.3'};
  color: ${p => p.$color || '#0f355b'};
  font-size: ${p => p.$size || '1.05rem'};
`;
// Bajada = descripción del módulo (mentor_modulos.descripcion).
const ModBajada = styled.div`
  margin-top: 5px; line-height: 1.4;
  color: ${p => p.$color || '#64748b'};
  font-size: ${p => p.$size || '0.82rem'};
`;
const IntroLabel = styled.div`
  font-size: ${p => p.$size || '0.82rem'}; color: ${p => p.$color || '#64748b'}; margin-bottom: 2px;
`;
const Pill = styled.div`
  display: inline-block; margin: 0.7rem 0 0.9rem;
  background: ${p => p.$bg || '#ede9fe'}; color: ${p => p.$color || '#6d28d9'};
  font-weight: 700; font-size: ${p => p.$size || '1.05rem'}; border-radius: 10px; padding: 0.35rem 1rem;
  /* Dentro de ModBody (flex en columna) los hijos se estiran a todo el ancho:
     el pill debe ceñirse a su texto. */
  width: auto;
  max-width: max-content;
  align-self: ${p => (p.$align === 'center' ? 'center' : 'flex-start')};
`;
// Título personalizado opcional debajo del pill "Módulo N" (icono estrella + texto)
const ModTituloCustom = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-weight: 700; line-height: 1.3;
  color: ${p => p.$color || '#f59e0b'};
  font-size: ${p => p.$size || '1rem'};
  .estrella { flex-shrink: 0; }
`;
// Descripción opcional debajo del título del módulo (mismo color que el título)
const ModDescCustom = styled.div`
  color: ${p => p.$color || '#f59e0b'};
  font-size: ${p => p.$size || '0.85rem'};
  line-height: 1.4; font-weight: 400; margin-top: 3px;
`;
const Sesion = styled.div`margin-bottom: 0.9rem;`;
const SesionLabel = styled.div`
  font-size: ${p => p.$size || '0.82rem'}; font-weight: 700; color: ${p => p.$color || '#db2777'}; margin-bottom: 2px;
`;
const SesionTitulo = styled.div`
  font-size: ${p => p.$size || '0.92rem'}; line-height: 1.4; color: ${p => p.$color || '#475569'}; margin-bottom: 2px;
`;
const DocenteRow = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  font-weight: 700; color: ${p => p.$color || '#1e293b'}; font-size: ${p => p.$size || '0.9rem'};
  img { width: 20px; height: 20px; border-radius: 50%; object-fit: cover; }
`;
const BtnWrap = styled.div`display: flex; justify-content: center; flex-wrap: wrap; gap: 0.8rem; margin-top: 2rem;`;
const BigBtn = styled.button`
  border: ${p => p.$border || 'none'}; cursor: pointer; font-family: inherit; font-weight: 700; color: ${p => p.$color || '#fff'};
  font-size: ${p => p.$size || '1.05rem'}; padding: ${p => p.$padding || '0.85rem 3rem'};
  border-radius: ${p => p.$radius || '999px'};
  background: ${p => p.$bg || 'linear-gradient(90deg, #db2777, #7c3aed)'};
  box-shadow: ${p => p.$shadow || '0 8px 24px rgba(124,58,237,0.35)'}; transition: 0.2s;
  &:hover:not(:disabled) { transform: translateY(-2px); opacity: 0.95; }
  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
`;

// ===== Formato 'cursos_tarjeta' (tarjeta oscura con foto de docente por sesión) =====
const TarjCard = styled.div`
  background: ${p => p.$bg || '#1a0b2e'};
  border-radius: ${p => p.$radius || '18px'};
  padding: 1.6rem 1.7rem;
  display: flex; flex-direction: column;
  ${p => p.$fill ? 'height: 100%;' : ''}
`;
const SesRow = styled.div`
  display: flex; align-items: flex-start; gap: 0.9rem; margin-bottom: 1.1rem;
`;
const SesFotoWrap = styled.div`position: relative; flex-shrink: 0;`;
const SesFoto = styled.div`
  width: ${p => p.$size || '46px'}; height: ${p => p.$size || '46px'}; border-radius: 50%;
  background-image: url(${p => p.$src}); background-size: cover; background-position: center;
  background-color: rgba(255,255,255,0.1);
`;
const SesFlag = styled.img`
  position: absolute; width: ${p => p.$size || '20px'}; height: ${p => p.$size || '20px'}; border-radius: 50%;
  object-fit: cover; border: 2px solid ${p => p.$cardBg || '#1a0b2e'}; background: #fff;
`;
// Posición de la banderita sobre la foto del docente.
const flagPosStyle = (pos) => {
  switch (pos) {
    case 'arriba':    return { top: -6, left: '50%', transform: 'translateX(-50%)' };
    case 'abajo':     return { bottom: -6, left: '50%', transform: 'translateX(-50%)' };
    case 'izquierda': return { top: '50%', left: -6, transform: 'translateY(-50%)' };
    case 'derecha':   return { top: '50%', right: -6, transform: 'translateY(-50%)' };
    default:          return { bottom: -2, right: -2 }; // esquina inferior derecha (diseño)
  }
};
const SesBody = styled.div`flex: 1; min-width: 0;`;
const SesNombre = styled.div`
  font-weight: 700; color: ${p => p.$color || '#fff'}; font-size: ${p => p.$size || '0.9rem'}; margin-top: 2px;
`;
const Divisor = styled.div`
  height: 1px; background: ${p => p.$color || 'rgba(255,255,255,0.15)'}; margin: 0.4rem 0 0.9rem;
`;
const PctText = styled.div`
  text-align: center; font-size: ${p => p.$size || '0.72rem'}; font-weight: 700; letter-spacing: 0.5px;
  color: ${p => p.$color || 'rgba(255,255,255,0.65)'}; margin-bottom: 0.7rem;
`;
const TarjBtnRow = styled.div`display: flex; gap: 0.5rem; justify-content: center; flex-wrap: wrap;`;
/* Atajo de QA: solo lo ven los usuarios marcados como "usuario de pruebas".
   Marca el módulo como visto para revisar avances sin reproducir los videos. */
const BtnPruebas = styled.button`
  background: rgba(251, 191, 36, 0.16);
  color: #92400e;
  border: 1px dashed rgba(180, 83, 9, 0.55);
  border-radius: 8px;
  padding: 0.35rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  &:hover { background: rgba(251, 191, 36, 0.3); }
`;

// Aviso de bloqueo de país a nivel de módulo (encima del contenido de la tarjeta).
const AvisoModulo = styled.div`
  display: flex; align-items: flex-start; gap: 6px;
  color: ${p => p.$color || '#5d09a8'};
  /* Borde 1px con degradado morado→fucsia oscuro sobre fondo blanco:
     capa padding-box = relleno; capa border-box = degradado del borde. */
  background:
    linear-gradient(${p => p.$bg || '#ffffff'}, ${p => p.$bg || '#ffffff'}) padding-box,
    ${p => p.$border || 'linear-gradient(90deg, #7c3aed, #a10a5c)'} border-box;
  border: 1px solid transparent;
  border-radius: 10px; padding: 0.5rem 0.7rem; margin: 0 0 0.9rem;
  font-size: ${p => p.$size || '0.8rem'}; font-weight: 600; line-height: 1.35;
`;
// Aviso a nivel de sesión/lección (más discreto, debajo del título de la sesión).
const AvisoSesion = styled.div`
  display: inline-flex; align-items: flex-start; gap: 5px;
  color: ${p => p.$color || '#5d09a8'};
  /* Mismo tratamiento que el aviso de módulo: fondo blanco + borde degradado. */
  background:
    linear-gradient(${p => p.$bg || '#ffffff'}, ${p => p.$bg || '#ffffff'}) padding-box,
    ${p => p.$border || 'linear-gradient(90deg, #7c3aed, #a10a5c)'} border-box;
  border: 1px solid transparent;
  border-radius: 8px; padding: 0.3rem 0.55rem; margin-top: 5px;
  font-size: ${p => p.$size || '0.76rem'}; font-weight: 600; line-height: 1.3;
`;

// Bandera para CUALQUIER país: resuelve nombre (es/alias) → ISO-2 con el
// catálogo completo de countryCodes; si ya viene un código ISO-2 lo usa directo.
const flagUrl = (pais, w = 40) => {
  if (!pais) return null;
  const found = buscarCodigoPais(pais);
  const code = found ? found.iso : (String(pais).trim().length === 2 ? String(pais).trim() : null);
  return code ? `https://flagcdn.com/w${w}/${code.toLowerCase()}.png` : null;
};

const resolveImg = (img) => !img ? null : (img.startsWith('http') ? img : `${BACKEND_BASE}/${img}`);

// ===== Disponibilidad por país (aviso en la ficha del landing) =====
// Une una lista de países en texto natural: "Argentina", "Argentina y Brasil",
// "Argentina, Brasil y Chile".
const unirPaises = (arr) => {
  const l = (arr || []).filter(Boolean);
  if (l.length === 0) return '';
  if (l.length === 1) return l[0];
  return l.slice(0, -1).join(', ') + ' y ' + l[l.length - 1];
};
// Aviso de una lección según su pais_modo: 'excluir' => no disponible para esos
// países; 'incluir' => solo disponible para esos. 'todos'/sin países => null.
const avisoLeccion = (lec) => {
  const modo = lec?.pais_modo || 'todos';
  const paises = Array.isArray(lec?.paises) ? lec.paises : [];
  if (modo === 'excluir' && paises.length) return { tipo: 'excluir', paises };
  if (modo === 'incluir' && paises.length) return { tipo: 'incluir', paises };
  return null;
};
// Países bloqueados por una lección (para calcular el bloqueo de módulo completo).
// Universo = países mencionados en el módulo; 'incluir' bloquea a todos menos los suyos.
const paisesBloqueadosLec = (lec, universo) => {
  const modo = lec?.pais_modo || 'todos';
  const paises = Array.isArray(lec?.paises) ? lec.paises : [];
  if (modo === 'excluir') return new Set(paises);
  if (modo === 'incluir') return new Set(universo.filter(p => !paises.includes(p)));
  return new Set(); // 'todos' no bloquea a nadie
};
// Países para los que TODAS las sesiones del módulo están bloqueadas (universo =
// países mencionados en cada lección).
const paisesBloqueoModulo = (lecs) => {
  const arr = lecs || [];
  if (arr.length === 0) return [];
  const universo = Array.from(new Set(arr.flatMap(l => (Array.isArray(l.paises) ? l.paises : []))));
  if (universo.length === 0) return [];
  return universo.filter(p => arr.every(l => paisesBloqueadosLec(l, universo).has(p)));
};

// Línea de docente reutilizable (nombre + bandera)
const DocenteLine = ({ nombre, pais, mostrarBandera, color, size }) => {
  if (!nombre) return null;
  const flag = mostrarBandera ? flagUrl(pais) : null;
  return (
    <DocenteRow $color={color} $size={size}>
      <span>{nombre}</span>
      {flag && <img src={flag} alt={pais || ''} onError={(e) => { e.target.style.display = 'none'; }} />}
    </DocenteRow>
  );
};

const CursosFichasSection = ({ config, proyecto, openLogin, tl }) => {
  const c = config;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Textos fijos de la sección (los configurables van por `tl`).
  const { t } = useIdioma();

  const documentos = proyecto?.documentos || [];

  // Progreso mentor por documento del usuario autenticado. proyecto-info.php es
  // público y NO trae progreso → lo pedimos aparte (endpoint con auth) y lo
  // fusionamos. Sin usuario logueado, todo queda en 0.
  const [progresoMap, setProgresoMap] = useState({});
  const [progresoModulos, setProgresoModulos] = useState({});
  const [evalMap, setEvalMap] = useState({});
  // Detalle {total, hechas} de sesiones por curso y por módulo. Con él la ficha
  // puede sumarle el examen como un paso más y saber si al alumno todavía le
  // faltan sesiones antes de habilitarle ese examen.
  const [leccionesDoc, setLeccionesDoc] = useState({});
  const [leccionesMod, setLeccionesMod] = useState({});
  const docIdsKey = documentos.map(d => d.id).join(',');
  // La barra por módulo se pide solo si la ficha la muestra: es un cálculo extra
  // en el backend y la mayoría de los diseños usa el avance del curso entero.
  const pctPorModulo = c.mostrar_pct_modulo === true;
  // Con `progreso_incluye_examen`, en los cursos que tienen evaluación el examen
  // cuenta como un paso más: el 100% del curso exige aprobarlo.
  const conExamen = c.progreso_incluye_examen === true;
  // El examen solo se habilita cuando el alumno ya vio las sesiones (opt-in:
  // las secciones ya publicadas siguen abriendo el examen desde el primer día).
  const evalRequiereLecciones = c.eval_requiere_lecciones === true;
  // El detalle por módulo se pide para pintar la barra y, aunque no se pinte,
  // cuando el examen depende de las sesiones de su módulo.
  // La etiqueta de estado se calcula con el avance de cada módulo, así que
  // también obliga a pedir el detalle por módulo.
  const pedirPorModulo = pctPorModulo || evalRequiereLecciones || c.mostrar_estado_modulo === true;
  const aplicarProgreso = useCallback((data) => {
    setProgresoMap(data.progreso || {});
    setProgresoModulos(data.modulos || {});
    setEvalMap(data.evaluacion || {});
    setLeccionesDoc(data.lecciones || {});
    setLeccionesMod(data.modulos_lecciones || {});
  }, []);
  useEffect(() => {
    if (!user || !docIdsKey) {
      aplicarProgreso({});
      return;
    }
    let vivo = true;
    mentor2Service.progresoProyecto(docIdsKey.split(','), { porModulo: pedirPorModulo, incluirExamen: conExamen })
      .then(res => {
        if (!vivo || !res.data?.success) return;
        aplicarProgreso(res.data);
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [user, docIdsKey, pedirPorModulo, conExamen, aplicarProgreso]);
  // Modo pruebas (QA): dar por visto un módulo entero desde la ficha, o
  // limpiarlo para dejarlo como si no se hubiera empezado y repetir la prueba.
  const [pruebasMsg, setPruebasMsg] = useState('');
  const accionModuloPruebas = async (documentId, moduloId, limpiar = false) => {
    try {
      setPruebasMsg(limpiar ? 'Limpiando…' : 'Marcando…');
      await mentor2Service.completarPrueba({ documentId, alcance: 'modulo', moduloId, limpiar });
      const res = await mentor2Service.progresoProyecto(docIdsKey.split(','), { porModulo: pedirPorModulo, incluirExamen: conExamen });
      if (res.data?.success) aplicarProgreso(res.data);
      setPruebasMsg('');
    } catch (err) {
      setPruebasMsg(err.response?.data?.message || (limpiar ? 'No se pudo limpiar' : 'No se pudo marcar'));
      setTimeout(() => setPruebasMsg(''), 3000);
    }
  };

  // % efectivo: el del endpoint autenticado, con fallback al que traiga el doc.
  const pctDoc = (d) => {
    const p = progresoMap[String(d.id)];
    return Number(p != null ? p : (d.progreso || 0));
  };
  // Estado de evaluación por documento (del endpoint autenticado).
  const evalHechaDoc = (d) => !!(evalMap[String(d.id)] && evalMap[String(d.id)].hecha);
  const evalAprobadaDocId = (docId) => !!(evalMap[String(docId)] && evalMap[String(docId)].aprobada);
  // ¿Ya vio todas las sesiones? `det` es {total, hechas} de un curso o de un
  // módulo. Sin dato todavía (petición en vuelo) no se da por cumplido.
  const leccionesListas = (det) => !!det && Number(det.total) > 0 && Number(det.hechas) >= Number(det.total);
  // "Ya vio el curso completo". Se mira el detalle de sesiones y no el
  // porcentaje, porque con `progreso_incluye_examen` el % nunca llega a 100
  // antes de aprobar el examen (y entonces exigir 100% para habilitarlo dejaría
  // al alumno encerrado). Sin detalle todavía, vale el porcentaje de siempre.
  const cursoVisto = (docId, pct) => {
    const det = leccionesDoc[String(docId)];
    return det ? leccionesListas(det) : Number(pct) >= 100;
  };

  if (documentos.length === 0) return null;

  const acento = c.color_acento || proyecto?.color_secundario || '#FF00FF';

  const go = (path) => {
    // Se lleva de dónde salió: así el botón «Contenidos» del reproductor
    // devuelve a esta misma página y no a la de contenidos del proyecto.
    if (user) navigate(path, { state: { volverA: location.pathname } });
    else if (openLogin) openLogin();
    else navigate('/login');
  };

  // ---- Avisos de disponibilidad por país (opt-in con "Mostrar bloqueo por país") ----
  const mostrarBloqueoPais = c.mostrar_bloqueo_pais === true;
  const txtNoDisp = c.texto_no_disponible || 'Esta sesión no está disponible para';
  const txtSoloDisp = c.texto_solo_disponible || 'Esta sesión solo está disponible para';
  const txtModuloNoDisp = c.texto_modulo_no_disponible || 'Este módulo no está disponible para';
  // Banner de módulo: solo si TODAS las sesiones (ya filtradas) bloquean al país.
  const renderAvisoModulo = (lecs) => {
    if (!mostrarBloqueoPais) return null;
    const paises = paisesBloqueoModulo(lecs);
    if (!paises.length) return null;
    return (
      <AvisoModulo $size={c.tamano_aviso_pais} $color={c.color_aviso_pais} $bg={c.color_aviso_pais_fondo} $border={c.color_aviso_pais_borde}>
        <span>⚠</span><span>{txtModuloNoDisp} {unirPaises(paises)}.</span>
      </AvisoModulo>
    );
  };
  // Aviso por sesión. Se omite cuando el módulo completo ya está bloqueado para
  // ese país (evita duplicar el mensaje: manda el banner del módulo).
  const renderAvisoSesion = (lec, bloqModulo) => {
    if (!mostrarBloqueoPais) return null;
    const av = avisoLeccion(lec);
    if (!av) return null;
    if (av.tipo === 'excluir') {
      // 'excluir': paises = bloqueados. Si el módulo entero ya avisa por ese
      // país (banner), no repetir la sesión.
      const restantes = av.paises.filter(p => !(bloqModulo || []).includes(p));
      if (restantes.length === 0) return null;
      return (
        <AvisoSesion $size={c.tamano_aviso_sesion} $color={c.color_aviso_pais}>
          <span>ⓘ</span><span>{txtNoDisp} {unirPaises(restantes)}.</span>
        </AvisoSesion>
      );
    }
    // 'incluir': paises = disponibles (solo esos países la ven).
    return (
      <AvisoSesion $size={c.tamano_aviso_sesion} $color={c.color_aviso_incluir || '#0891b2'}>
        <span>ⓘ</span><span>{txtSoloDisp} {unirPaises(av.paises)}.</span>
      </AvisoSesion>
    );
  };

  // ============ FORMATO 'cursos_tarjeta' (tarjeta oscura, foto docente por sesión) ============
  if (c.formato === 'cursos_tarjeta') {
    const cols = parseInt(c.columnas) || 2;
    const lecIds = Array.isArray(c.lecciones_ids) ? c.lecciones_ids.map(String) : [];
    const lecSet = lecIds.length > 0 ? new Set(lecIds) : null;
    const filtrar = (lecs) => lecSet ? (lecs || []).filter(l => lecSet.has(String(l.id))) : (lecs || []);

    const docsById = new Map(documentos.map(d => [String(d.id), d]));
    const orden = Array.isArray(c.cursos_ids) ? c.cursos_ids.map(String) : [];
    const cursosSel = orden.length > 0 ? orden.map(id => docsById.get(id)).filter(Boolean) : documentos;
    const bloques = cursosSel.map(d => {
      const lecs = (d.modulos || []).flatMap(m => filtrar(m.lecciones).map(l => ({
        ...l,
        _foto: l.docente_foto || m.docente_foto,
        _nombre: l.docente_nombre || m.docente_nombre,
        _pais: l.docente_pais || m.docente_pais,
      })));
      return { doc: d, lecs };
    }).filter(b => b.lecs.length > 0);
    if (bloques.length === 0) return null;

    const mostrarFoto = c.mostrar_foto_docente !== false;
    const mostrarBandera = c.mostrar_bandera !== false;
    const mostrarPct = c.mostrar_pct !== false;
    const mostrarDocNombre = c.mostrar_docentes !== false;
    const etiquetaLeccion = c.etiqueta_leccion || 'Sesión';
    const cardBg = c.color_tarjeta || '#1a0b2e';
    const btnBg = c.color_boton || 'linear-gradient(90deg, #db2777, #7c3aed)';
    const modos = proyecto?.config_json?.modos_habilitados || {};

    return (
      <Wrapper $bg={buildBg(c, '#0b0218')} $padding={c.padding}>
        {(c.titulo || c.subtitulo) && (
          <Header $align={c.alineacion_titulo || 'center'}>
            {c.titulo && <Title $size={c.tamano_titulo} $color={c.color_titulo || '#fff'} dangerouslySetInnerHTML={{ __html: tl(c.titulo) }} />}
            {c.subtitulo && <Subtitle $size={c.tamano_subtitulo} $color={c.color_subtitulo} dangerouslySetInnerHTML={{ __html: tl(c.subtitulo) }} />}
          </Header>
        )}
        <ModGrid $cols={cols} $gap={c.gap} $maxWidth={c.ancho_maximo} $igualAltura={c.igualar_altura_modulos === true}>
          {bloques.map(b => {
            const d = b.doc;
            const pct = pctDoc(d);
            const showIngresar = c.mostrar_boton_ingresar !== false;
            const showEvalBase = Number(d.modo_evaluacion) === 1 && modos.evaluacion !== false && c.mostrar_boton_eval !== false;
            // Nuevo: el botón de evaluación puede requerir el modo mentor al 100%.
            const cursoListo = cursoVisto(d.id, pct);
            const showEval = showEvalBase && (!c.eval_requiere_mentor || cursoListo);
            const evalPendiente = showEvalBase && !evalHechaDoc(d) && (!c.eval_requiere_mentor || cursoListo);
            const glowEval = c.resaltar_eval_pendiente === true && showEval && !evalHechaDoc(d);
            const legendaEval = c.resaltar_eval_pendiente === true && evalPendiente && cursoListo;
            const showReto = Number(d.modo_reto) === 1 && modos.reto !== false && c.mostrar_boton_reto !== false;
            const showPractica = Number(d.modo_practica) === 1 && modos.practica === true && c.mostrar_boton_practica !== false;
            // Título/descripción personalizados: la tarjeta es por documento; se
            // busca el primer módulo del documento que tenga título/descripción.
            const mapaT = c.titulos_modulo || {};
            const mapaD = c.descripciones_modulo || {};
            const modIds = (d.modulos || []).map(m => m.id);
            const pickBy = (mapa) => { for (const id of modIds) { const v = mapa[id] != null ? mapa[id] : mapa[String(id)]; if (v && tl(v)) return v; } return null; };
            const customTxt = c.mostrar_titulo_modulo ? pickBy(mapaT) : null;
            const customDesc = c.mostrar_titulo_modulo ? pickBy(mapaD) : null;
            const tituloComoPill = c.titulo_como_pill === true && customTxt;
            const starColor = c.color_estrella_modulo || c.color_titulo_modulo || '#f59e0b';
            const tituloColor = c.color_titulo_modulo || '#f59e0b';
            return (
              <TarjCard key={d.id} $bg={cardBg} $radius={c.radio_tarjeta} $fill={c.igualar_altura_modulos === true}>
                <Pill $size={c.tamano_pill} $bg={c.color_pill_fondo || '#ede9fe'} $color={c.color_pill_texto || '#6d28d9'} style={{ alignSelf: 'flex-start' }}>
                  {tituloComoPill
                    ? (<><span style={{ color: starColor, marginRight: 6 }}>★</span><span dangerouslySetInnerHTML={{ __html: tl(customTxt) }} /></>)
                    : tl(conI18n(d, 'titulo'))}
                </Pill>
                {c.mostrar_titulo_modulo && ((customTxt && !tituloComoPill) || customDesc) && (
                  <div style={{ margin: '-0.3rem 0 0.9rem' }}>
                    {customTxt && !tituloComoPill && (
                      <ModTituloCustom $color={tituloColor} $size={c.tamano_titulo_modulo || '1rem'}>
                        <span className="estrella" style={{ color: starColor }}>★</span>
                        <span dangerouslySetInnerHTML={{ __html: tl(customTxt) }} />
                      </ModTituloCustom>
                    )}
                    {customDesc && <ModDescCustom $color={tituloColor} $size={c.tamano_desc_modulo || '0.85rem'} dangerouslySetInnerHTML={{ __html: tl(customDesc) }} />}
                  </div>
                )}
                {b.lecs.map((lec, i) => {
                  const foto = resolveImg(lec._foto);
                  const flag = mostrarBandera ? flagUrl(lec._pais) : null;
                  return (
                    <SesRow key={lec.id}>
                      {mostrarFoto && (
                        <SesFotoWrap>
                          <SesFoto $src={foto} $size={c.tamano_foto} />
                          {flag && <SesFlag src={flag} $cardBg={cardBg} $size={c.tamano_bandera} style={flagPosStyle(c.posicion_bandera)} alt="" onError={(e) => { e.target.style.display = 'none'; }} />}
                        </SesFotoWrap>
                      )}
                      <SesBody>
                        <SesionLabel $size={c.tamano_sesion_label} $color={c.color_sesion_label || acento}>{etiquetaLeccion} {i + 1}</SesionLabel>
                        {lec.titulo && <SesionTitulo $size={c.tamano_titulo_sesion} $color={c.color_titulo_sesion || 'rgba(255,255,255,0.8)'} dangerouslySetInnerHTML={{ __html: tl(conI18n(lec, 'titulo')) }} />}
                        {mostrarDocNombre && lec._nombre && <SesNombre $size={c.tamano_docente} $color={c.color_docente || '#fff'}>{lec._nombre}</SesNombre>}
                      </SesBody>
                    </SesRow>
                  );
                })}
                {/* footer pegado abajo → tarjetas de una misma fila quedan niveladas */}
                <div style={{ marginTop: 'auto' }}>
                  <Divisor $color={c.color_divisor} />
                  {mostrarPct && <PctText $size={c.tamano_pct} $color={c.color_pct}>{pct}% COMPLETADO</PctText>}
                  {legendaEval && <PendienteEval $color={c.color_pendiente_eval || c.color_boton_alt || acento} $size={c.tamano_pct}>⚠ {tl(c.texto_pendiente_eval) || 'Pendiente evaluación'}</PendienteEval>}
                  <TarjBtnRow>
                    {showIngresar && <Btn $size={c.tamano_boton_ficha} $bg={btnBg} onClick={() => go(d.id ? `/mentor/${d.id}` : '/contenidos')}>{tl(c.texto_boton) || 'Ingresar'}</Btn>}
                    {showEval && <BtnAlt $size={c.tamano_boton_ficha} $color={c.color_boton_alt || acento} $glow={glowEval} onClick={() => go(`/evaluacion/${d.id}`)}>{t('evaluacion')}</BtnAlt>}
                    {showReto && <BtnAlt $size={c.tamano_boton_ficha} $color={c.color_boton_alt || acento} onClick={() => go(`/reto/${d.id}`)}>{t('reto')}</BtnAlt>}
                    {showPractica && <BtnAlt $size={c.tamano_boton_ficha} $color={c.color_boton_alt || acento} onClick={() => go(`/consulta/${d.id}?modo=practica`)}>{t('practica')}</BtnAlt>}
                  </TarjBtnRow>
                </div>
              </TarjCard>
            );
          })}
        </ModGrid>
      </Wrapper>
    );
  }

  // ============ FORMATOS DE BLOQUES: 'modulos' y 'cursos_separados' ============
  // 'modulos'            → todos los módulos del proyecto en una sola grilla.
  // 'cursos_separados'   → cursos elegidos (y ordenados) por el admin, cada uno
  //                        con su título y su propia grilla de módulos (mismo look).
  if (c.formato === 'modulos' || c.formato === 'cursos_separados' || c.formato === 'modulos_separados') {
    const esSeparados = c.formato === 'modulos_separados';
    // El catálogo puede ir alineado a la izquierda (por defecto) o centrado.
    const alineacionTarjeta = c.alineacion_tarjeta || 'left';
    // Modos habilitados por rol del proyecto: los usan los botones de acceso.
    const modos = proyecto?.config_json?.modos_habilitados || {};
    const cols = parseInt(c.columnas) || (esSeparados ? 3 : 2);
    // En el diseño de catálogo la tarjeta es portada + pill + título: la intro y
    // las sesiones sobran, así que aquí son opt-in (en los demás formatos, opt-out).
    const mostrarIntro = esSeparados ? c.mostrar_intro === true : c.mostrar_intro !== false;
    const mostrarLecciones = esSeparados ? c.mostrar_lecciones === true : c.mostrar_lecciones !== false;
    const mostrarDocentes = c.mostrar_docentes !== false;
    const mostrarBandera = c.mostrar_bandera !== false;
    const etiquetaIntro = c.etiqueta_intro != null ? c.etiqueta_intro : 'Introducción y bienvenida al módulo';
    // Vacío es una opción válida: el pill queda solo con el número ("1", "2").
    // `tl`: el texto puede venir traducido ({es, pt}); vacío a propósito = sin etiqueta.
    const etiquetaModulo = c.etiqueta_modulo != null ? tl(c.etiqueta_modulo) : 'Módulo';
    // Portada por módulo: mapa id_modulo -> url, guardado en el JSON de la
    // sección (mentor_modulos no tiene columna de imagen).
    const mapaImg = c.imagenes_modulo || {};
    const mostrarBajada = c.mostrar_bajada !== false;
    const etiquetaLeccion = c.etiqueta_leccion || 'Sesión';
    // El diseño de catálogo usa pill teal; los otros formatos, lila.
    const pillBg = c.color_pill_fondo || (esSeparados ? '#14b8a6' : '#ede9fe');
    const pillColor = c.color_pill_texto || (esSeparados ? '#ffffff' : '#6d28d9');

    // Filtro de lecciones (para 'cursos_separados'): vacío = todas.
    const lecIds = Array.isArray(c.lecciones_ids) ? c.lecciones_ids.map(String) : [];
    const lecSet = lecIds.length > 0 ? new Set(lecIds) : null;
    const filtrarLecciones = (lecs) => {
      const arr = lecs || [];
      return lecSet ? arr.filter(l => lecSet.has(String(l.id))) : arr;
    };

    // El examen es del CURSO, no de cada módulo: `eval_en_modulos` decide dónde
    // sale ese botón. Por defecto 'todos' = como venía, para no cambiarle nada a
    // las secciones ya publicadas.
    //   todos   → en cada tarjeta de módulo
    //   ultimo  → solo en la última tarjeta del curso
    //   modulo  → solo en el módulo elegido en `eval_modulo_id` (el examen cubre
    //             ese módulo aunque en la base cuelgue del curso)
    //   seccion → un único botón debajo de la cuadrícula
    //   ninguno → no se muestra
    const dondeEval = c.eval_en_modulos || 'todos';
    // Leyenda del examen bloqueado. Por defecto habla del módulo o del curso
    // según a qué se le exijan las sesiones (ver renderModCard).
    const txtEvalBloqueada = tl(c.texto_eval_bloqueada)
      || (dondeEval === 'todos'
          ? 'Disponible al ver todas las sesiones del módulo'
          : 'Disponible al ver todas las sesiones del curso');
    // Se llenan justo antes de construir el cuerpo, con los módulos que de
    // verdad se van a pintar (el admin puede elegir un subconjunto).
    const ultimoDelCurso = {};   // docId -> id del último módulo pintado
    const cursosConEval = [];    // documentos con evaluación presentes en la sección

    // Recorre los módulos que se van a pintar, en orden, y anota cuál es el
    // último de cada curso y qué cursos tienen examen.
    const registrarCursos = (mods) => {
      mods.forEach(m => {
        const docId = String(m._docId);
        ultimoDelCurso[docId] = String(m.id);
        const doc = documentos.find(d => String(d.id) === docId);
        if (doc && Number(doc.modo_evaluacion) === 1 && modos.evaluacion !== false
            && !cursosConEval.some(d => String(d.id) === docId)) {
          cursosConEval.push(doc);
        }
      });
    };

    const renderModCard = (mod, idx, pillLabel) => {
      const lecs = filtrarLecciones(mod.lecciones);
      const bloqModulo = paisesBloqueoModulo(lecs);
      // Botones de acceso: dependen del documento (curso) dueño del módulo, que
      // es quien tiene los modos. Solo con sesión iniciada — a un visitante no
      // le sirve un botón que lo mandaría al login.
      const docMod = documentos.find(d => String(d.id) === String(mod._docId));
      const modMentor = docMod && Number(docMod.modo_mentor) === 1 && modos.mentor !== false;
      const cursoTieneEval = docMod && Number(docMod.modo_evaluacion) === 1 && modos.evaluacion !== false;
      const modEval = cursoTieneEval && (
        dondeEval === 'todos'
        || (dondeEval === 'ultimo' && String(ultimoDelCurso[String(mod._docId)]) === String(mod.id))
        || (dondeEval === 'modulo' && String(c.eval_modulo_id) === String(mod.id))
      );
      const mostrarAcceso = c.mostrar_boton_acceder === true && !!user && (modMentor || modEval);
      // ---- Examen dentro de la tarjeta del módulo ----
      // Qué sesiones hay que ver antes de presentarlo: las del módulo cuando el
      // botón sale en cada tarjeta ('todos'), y las del curso completo cuando el
      // botón representa al curso ('ultimo' / 'modulo'), porque el examen es del
      // curso y ahí cubre también los módulos anteriores.
      const detMod = leccionesMod[String(mod.id)];
      const detExamen = dondeEval === 'todos' ? detMod : leccionesDoc[String(mod._docId)];
      const examenListo = leccionesListas(detExamen);
      const examenBloqueado = modEval && evalRequiereLecciones && !examenListo;
      const examenAprobado = modEval && evalAprobadaDocId(mod._docId);
      // Título/descripción personalizados por módulo
      const mapaT = c.titulos_modulo || {};
      const mapaD = c.descripciones_modulo || {};
      const customTxt = mapaT[mod.id] != null ? mapaT[mod.id] : mapaT[String(mod.id)];
      const customDesc = mapaD[mod.id] != null ? mapaD[mod.id] : mapaD[String(mod.id)];
      const tieneTitulo = c.mostrar_titulo_modulo && customTxt && tl(customTxt);
      // Si está activo "usar título como pill", el pill muestra el título
      // personalizado (con ★) en vez de "Módulo N" / título del curso.
      const tituloComoPill = c.titulo_como_pill === true && tieneTitulo;
      const starColor = c.color_estrella_modulo || c.color_titulo_modulo || '#f59e0b';
      const pillTexto = tituloComoPill ? tl(customTxt) : (pillLabel != null ? pillLabel : `${etiquetaModulo} ${idx + 1}`.trim());
      // Portada del módulo (solo 'modulos_separados'): sin imagen la tarjeta
      // sigue funcionando, simplemente arranca en el pill.
      const imgMod = esSeparados
        ? resolveImg(mapaImg[mod.id] != null ? mapaImg[mod.id] : mapaImg[String(mod.id)])
        : null;
      // Bajada: manda la descripción del módulo (Curso Mentor) y, si está vacía,
      // el texto escrito para esta landing. Así se puede publicar sin tener que
      // ir a llenar descripciones en el curso.
      const bajada = mod.descripcion ? conI18n(mod, 'descripcion') : customDesc;
      const contenido = (
        <>
          {mostrarIntro && (
            <>
              {etiquetaIntro && <IntroLabel $size={c.tamano_intro_label} $color={c.color_intro_label || '#64748b'}>{etiquetaIntro}</IntroLabel>}
              {mostrarDocentes && (
                <DocenteLine nombre={mod.docente_nombre} pais={mod.docente_pais} mostrarBandera={mostrarBandera} color={c.color_docente || '#1e293b'} size={c.tamano_docente} />
              )}
            </>
          )}
          {c.mostrar_pill !== false && (
          <Pill $size={c.tamano_pill} $bg={pillBg} $color={pillColor} $align={alineacionTarjeta} style={esSeparados ? { marginTop: 0 } : undefined}>
            {tituloComoPill
              ? (<><span style={{ color: starColor, marginRight: 6 }}>★</span><span dangerouslySetInnerHTML={{ __html: pillTexto }} /></>)
              : pillTexto}
          </Pill>
          )}
          {esSeparados && mod.titulo && c.titulo_sobre_imagen !== true && (
            <ModTitulo $color={c.color_nombre_modulo} $size={c.tamano_nombre_modulo} $lineHeight={c.interlineado_nombre_modulo} dangerouslySetInnerHTML={{ __html: tl(conI18n(mod, 'titulo')) }} />
          )}
          {esSeparados && mostrarBajada && bajada && (
            <ModBajada $color={c.color_bajada_modulo} $size={c.tamano_bajada_modulo} dangerouslySetInnerHTML={{ __html: tl(bajada) }} />
          )}
          {c.mostrar_titulo_modulo && (() => {
            const desc = customDesc;
            // El título ★ separado solo se muestra si NO se usó como pill.
            const hayTxt = tieneTitulo && !tituloComoPill;
            // En 'modulos_separados' esta descripción ya salió como bajada arriba.
            const hayDesc = !esSeparados && desc && tl(desc);
            if (!hayTxt && !hayDesc) return null;
            const color = c.color_titulo_modulo || '#f59e0b';
            return (
              <div style={{ margin: '-0.3rem 0 0.9rem' }}>
                {hayTxt && (
                  <ModTituloCustom $color={color} $size={c.tamano_titulo_modulo || '1rem'}>
                    <span className="estrella" style={{ color: starColor }}>★</span>
                    <span dangerouslySetInnerHTML={{ __html: tl(customTxt) }} />
                  </ModTituloCustom>
                )}
                {hayDesc && (
                  <ModDescCustom $color={color} $size={c.tamano_desc_modulo || '0.85rem'} dangerouslySetInnerHTML={{ __html: tl(desc) }} />
                )}
              </div>
            );
          })()}
          {mostrarLecciones && renderAvisoModulo(lecs)}
          {mostrarLecciones && lecs.map((lec, li) => (
            <Sesion key={lec.id}>
              <SesionLabel $size={c.tamano_sesion_label} $color={c.color_sesion_label || acento}>{etiquetaLeccion} {li + 1}</SesionLabel>
              {lec.titulo && <SesionTitulo $size={c.tamano_titulo_sesion} $color={c.color_titulo_sesion || '#475569'} dangerouslySetInnerHTML={{ __html: tl(conI18n(lec, 'titulo')) }} />}
              {mostrarDocentes && (
                <DocenteLine nombre={lec.docente_nombre} pais={lec.docente_pais} mostrarBandera={mostrarBandera} color={c.color_docente || '#1e293b'} size={c.tamano_docente} />
              )}
              {renderAvisoSesion(lec, bloqModulo)}
            </Sesion>
          ))}
          {esSeparados && pctPorModulo && (() => {
            // Con `progreso_incluye_examen`, el examen que se presenta desde
            // esta tarjeta es un paso más del módulo: 3 sesiones + examen = 4
            // pasos, así que verlas todas deja el módulo en 75% y el 100% llega
            // al aprobar. Sin el examen (o sin la opción) manda el % del backend.
            let pctMod = Number(progresoModulos[String(mod.id)] || 0);
            if (conExamen && modEval && detMod) {
              const total = Number(detMod.total) + 1;
              const hechas = Number(detMod.hechas) + (examenAprobado ? 1 : 0);
              pctMod = total > 0 ? Math.round((hechas / total) * 100) : 0;
            }
            return (
              // width:100% para que la barra ocupe todo el ancho de la tarjeta:
              // con la tarjeta centrada (align-items:center) el bloque se
              // encogeria al ancho del texto "0% Completado".
              <div style={{ marginTop: 'auto', paddingTop: '0.9rem', width: '100%' }}>
                <ModBarra $bg={c.color_barra_modulo} $alto={c.alto_barra_modulo}>
                  <ModBarraFill $pct={pctMod} $color={c.color_barra_modulo_avance || acento} />
                </ModBarra>
                <ModPct $size={c.tamano_pct} $color={c.color_pct}>
                  {pctMod}% {tl(c.texto_pct) || 'Completado'}
                </ModPct>
              </div>
            );
          })()}
          {mostrarAcceso && (
            // marginTop:auto empuja los botones al fondo de la tarjeta: con las
            // alturas igualadas, todos quedan a la misma línea aunque el título
            // de un módulo ocupe más renglones que otro. Con la barra de avance
            // ya es ella la que ocupa ese hueco.
            <BtnRow style={{
              // Por defecto los botones se van al fondo de la tarjeta, para que
              // queden a la misma línea con las alturas igualadas. El diseño
              // puede pedir otra cosa con `margen_botones` / `padding_botones`.
              marginTop: c.margen_botones !== undefined ? c.margen_botones : ((esSeparados && pctPorModulo) ? 0 : 'auto'),
              paddingTop: c.padding_botones !== undefined ? c.padding_botones : '0.9rem',
              justifyContent: esSeparados ? (alineacionTarjeta === 'center' ? 'center' : 'flex-start') : undefined,
            }}>
              {modMentor && (
                <Btn
                  $size={c.tamano_boton_acceder}
                  $peso={c.peso_boton_acceder}
                  $padding={c.padding_boton_acceder}
                  $bg={c.color_boton_acceder || acento}
                  $color={c.color_texto_boton_acceder}
                  $border={c.borde_boton_acceder}
                  $radius={c.radio_boton_acceder}
                  // La tarjeta es de un módulo: el botón entra al curso ANCLADO
                  // en ese módulo (MentorPage lee ?modulo y abre ahí). Si el
                  // módulo todavía está bloqueado, el curso abre donde iba.
                  onClick={() => go(`/mentor/${docMod.id}?modulo=${mod.id}`)}
                >
                  {tl(c.texto_boton_acceder) || 'Acceder'}
                </Btn>
              )}
              {modEval && (
                // Por defecto el botón de evaluación va en versión "alterna"
                // (contorno). Con `boton_eval_como_acceder` toma exactamente el
                // mismo diseño que el de entrar al curso.
                // Mientras el examen siga bloqueado el botón se pinta apagado y
                // no navega; el candado lo delata y el motivo va en el `title`.
                c.boton_eval_como_acceder === true ? (
                  <Btn
                    $size={c.tamano_boton_acceder}
                    $bg={c.color_boton_acceder || acento}
                    $color={c.color_texto_boton_acceder}
                    $border={c.borde_boton_acceder}
                    $radius={c.radio_boton_acceder}
                    disabled={examenBloqueado}
                    title={examenBloqueado ? txtEvalBloqueada : undefined}
                    onClick={() => { if (!examenBloqueado) go(`/evaluacion/${docMod.id}`); }}
                  >
                    {examenBloqueado ? '🔒 ' : ''}{tl(c.texto_boton_eval_modulo) || 'Evaluación'}
                  </Btn>
                ) : (
                  <BtnAlt
                    $size={c.tamano_boton_acceder}
                    $color={c.color_boton_eval_modulo || acento}
                    disabled={examenBloqueado}
                    title={examenBloqueado ? txtEvalBloqueada : undefined}
                    onClick={() => { if (!examenBloqueado) go(`/evaluacion/${docMod.id}`); }}
                  >
                    {examenBloqueado ? '🔒 ' : ''}{tl(c.texto_boton_eval_modulo) || 'Evaluación'}
                  </BtnAlt>
                )
              )}
              {user?.modo_pruebas ? (
                <>
                  <BtnPruebas
                    title="Modo pruebas: marca este módulo como visto"
                    onClick={() => accionModuloPruebas(docMod.id, mod.id)}
                  >
                    ✓ Pruebas
                  </BtnPruebas>
                  <BtnPruebas
                    title="Modo pruebas: borra el avance de este módulo y lo deja sin empezar"
                    onClick={() => accionModuloPruebas(docMod.id, mod.id, true)}
                  >
                    ↺ Limpiar
                  </BtnPruebas>
                </>
              ) : null}
            </BtnRow>
          )}
          {user?.modo_pruebas && pruebasMsg ? (
            <div style={{ fontSize: '0.7rem', color: '#92400e', textAlign: 'center', marginTop: '0.3rem' }}>{pruebasMsg}</div>
          ) : null}
        </>
      );
      return (
        <ModCard
          key={mod.id}
          $bg={c.color_tarjeta || '#ffffff'}
          $border={c.color_borde_tarjeta}
          $radius={c.radio_tarjeta}
          $sombra={c.sombra_tarjeta}
          $sinRecorte={c.estilo_estado === 'cinta'}
          $fill={c.igualar_altura_modulos === true}
          $noPad={esSeparados}
        >
          {esSeparados && imgMod && (() => {
            // Estado y título pueden ir SOBRE la portada (opt-in): sin esas
            // claves la tarjeta es la de siempre, imagen y debajo el texto.
            const conEstado = c.mostrar_estado_modulo === true;
            const esCinta = c.estilo_estado === 'cinta';
            const conTitulo = c.titulo_sobre_imagen === true && mod.titulo;
            const avance = Number(progresoModulos[String(mod.id)] || 0);
            const estado = avance >= 100
              ? (tl(c.texto_estado_completado) || 'Completado')
              : (avance > 0 ? (tl(c.texto_estado_en_curso) || 'En curso')
                            : (tl(c.texto_estado_sin_iniciar) || 'Sin iniciar'));
            const mapaFranja = c.colores_franja_modulo || {};
            const franja = mapaFranja[mod.id] != null ? mapaFranja[mod.id] : mapaFranja[String(mod.id)];
            return (
              <ModImg
                $src={imgMod}
                $h={c.altura_imagen}
                $conEncima={conEstado || conTitulo}
                $cintaFuera={esCinta}
                $radio={esCinta ? (c.radio_tarjeta || '10px') : null}
              >
                {conEstado && (
                  <ModEstado
                    $bg={c.color_estado_fondo}
                    $color={c.color_estado_texto}
                    $size={c.tamano_estado}
                    $peso={c.peso_estado}
                    $padding={c.padding_estado}
                    $radio={c.radio_estado}
                    $cinta={esCinta}
                    $saliente={c.saliente_estado}
                    $separacion={c.separacion_estado}
                    $pliegue={c.color_estado_pliegue}
                    $pliegueGrosor={c.grosor_pliegue_estado}
                    $pliegueAlto={c.alto_pliegue_estado}
                  >{estado}</ModEstado>
                )}
                {conTitulo && (
                  <ModTituloSobre
                    $bg={franja || c.color_franja_titulo}
                    $color={c.color_texto_franja}
                    $size={c.tamano_nombre_modulo}
                    $lineHeight={c.interlineado_nombre_modulo}
                    $align={c.alineacion_franja || alineacionTarjeta}
                    $padding={c.padding_franja}
                    $radio={esCinta ? (c.radio_tarjeta || '10px') : null}
                    $bgHover={c.color_franja_hover}
                    $paddingHover={c.padding_franja_hover}
                    $subida={c.subida_franja_hover}
                    dangerouslySetInnerHTML={{ __html: tl(conI18n(mod, 'titulo')) }}
                  />
                )}
              </ModImg>
            );
          })()}
          {esSeparados
            ? <ModBody $padding={c.padding_tarjeta} $align={alineacionTarjeta} $fill={c.igualar_altura_modulos === true}>{contenido}</ModBody>
            : contenido}
        </ModCard>
      );
    };

    const handleBoton = () => {
      const acc = c.accion_boton || 'login';
      if (acc === 'contenidos') go('/contenidos');
      else if (acc && acc.startsWith('http')) window.open(acc, '_blank');
      else if (openLogin && !user) openLogin();
      else go('/contenidos');
    };

    // ----- Construir los bloques según formato -----
    let cuerpo;
    if (c.formato === 'cursos_separados') {
      const docsById = new Map(documentos.map(d => [String(d.id), d]));
      const orden = Array.isArray(c.cursos_ids) ? c.cursos_ids.map(String) : [];
      const cursosSel = orden.length > 0
        ? orden.map(id => docsById.get(id)).filter(Boolean)
        : documentos;
      // Solo cursos con al menos un módulo con lecciones (tras el filtro).
      const bloques = cursosSel
        // _docId: renderModCard lo usa para ubicar el curso dueño del módulo
        // (modos mentor/evaluación y destino de los botones de acceso).
        .map(d => ({ doc: d, mods: (d.modulos || []).map(m => ({ ...m, _docId: d.id })) }))
        .filter(b => b.mods.length > 0);
      if (bloques.length === 0) return null;
      registrarCursos(bloques.flatMap(b => b.mods));
      const mostrarTituloCurso = c.mostrar_titulo_curso === true;
      // Cada curso es una celda de la grilla: las columnas aplican a los cursos
      // (2 columnas → 2 cursos por fila). Dentro, sus módulos se apilan.
      cuerpo = (
        <ModGrid $cols={cols} $gap={c.gap_cursos || c.gap} $maxWidth={c.ancho_maximo} $igualAltura={c.igualar_altura_modulos === true}>
          {bloques.map((b) => (
            <CursoCol key={b.doc.id} $gap={c.gap} $fill={c.igualar_altura_modulos === true}>
              {mostrarTituloCurso && b.doc.titulo && (
                <CursoTitulo
                  $size={c.tamano_titulo_curso || '20px'}
                  $color={c.color_titulo_curso || c.color_titulo || '#0f355b'}
                  $align={c.alineacion_titulo || 'left'}
                  dangerouslySetInnerHTML={{ __html: tl(conI18n(b.doc, 'titulo')) }}
                />
              )}
              {b.mods.map((mod, idx) => renderModCard(mod, idx, tl(conI18n(b.doc, 'titulo'))))}
            </CursoCol>
          ))}
        </ModGrid>
      );
    } else {
      let modulos = documentos.flatMap(d => (d.modulos || []).map(m => ({ ...m, _docId: d.id })));
      // 'modulos_separados': el admin elige y ordena los módulos del catálogo.
      // Vacío = todos (misma semántica que cursos_ids / lecciones_ids).
      const modSel = Array.isArray(c.modulos_ids) ? c.modulos_ids.map(String) : [];
      if (esSeparados && modSel.length > 0) {
        const modById = new Map(modulos.map(m => [String(m.id), m]));
        modulos = modSel.map(id => modById.get(id)).filter(Boolean);
      }
      if (modulos.length === 0) return null;
      registrarCursos(modulos);
      cuerpo = (
        <ModGrid $cols={cols} $gap={c.gap} $maxWidth={c.ancho_maximo} $igualAltura={c.igualar_altura_modulos === true}>
          {modulos.map((mod, idx) => renderModCard(mod, idx))}
        </ModGrid>
      );
    }

    return (
      <Wrapper
        $bg={buildBg(c, '#f6f7fb')}
        $padding={c.padding}
        $bgImage={c.imagen_fondo ? resolveImg(c.imagen_fondo) : null}
        $overlay={c.overlay_fondo}
        $bgSize={c.tamano_fondo}
        $bgPos={c.posicion_fondo}
      >
        {(c.titulo || c.subtitulo) && (
          <Header $align={c.alineacion_titulo || 'center'}>
            {c.titulo && <Title $size={c.tamano_titulo} $color={c.color_titulo || '#0f355b'} dangerouslySetInnerHTML={{ __html: tl(c.titulo) }} />}
            {c.subtitulo && <Subtitle $size={c.tamano_subtitulo} $color={c.color_subtitulo} dangerouslySetInnerHTML={{ __html: tl(c.subtitulo) }} />}
          </Header>
        )}
        {cuerpo}
        {/* Examen del curso completo: un solo botón bajo la cuadrícula, en vez
            de repetirlo en cada módulo. Solo con sesión iniciada, igual que los
            botones de las tarjetas. */}
        {dondeEval === 'seccion' && !!user && cursosConEval.length > 0 && (
          <BtnWrap>
            {cursosConEval.map(doc => {
              // Mismo criterio que en las tarjetas: acá el examen es del curso,
              // así que se exigen todas sus sesiones.
              const bloq = evalRequiereLecciones && !leccionesListas(leccionesDoc[String(doc.id)]);
              return (
              <BigBtn
                key={`evalcurso${doc.id}`}
                $bg={c.color_boton_eval_curso || c.color_boton_acceder || acento}
                $color={c.color_texto_boton_eval_curso || c.color_texto_boton_acceder}
                $border={c.borde_boton_eval_curso || c.borde_boton_acceder}
                $radius={c.radio_boton_eval_curso || c.radio_boton_acceder}
                $padding={c.padding_boton_eval_curso}
                $shadow={c.sombra_boton_eval_curso}
                $size={c.tamano_boton_eval_curso || c.tamano_boton_acceder}
                disabled={bloq}
                title={bloq ? txtEvalBloqueada : undefined}
                onClick={() => { if (!bloq) go(`/evaluacion/${doc.id}`); }}
              >
                {bloq ? '🔒 ' : ''}{tl(c.texto_boton_eval_curso) || 'Presentar evaluación'}
              </BigBtn>
              );
            })}
          </BtnWrap>
        )}
        {/* El catálogo no lleva botón general en el diseño → opt-in aquí. */}
        {(esSeparados ? c.mostrar_boton === true : c.mostrar_boton !== false) && (
          <BtnWrap>
            <BigBtn
              $bg={c.color_boton}
              $color={c.color_texto_boton}
              $radius={c.radio_boton}
              $padding={c.padding_boton}
              $size={c.tamano_boton}
              onClick={handleBoton}
            >
              {tl(c.texto_boton) || 'Ingresar'}
            </BigBtn>
          </BtnWrap>
        )}
      </Wrapper>
    );
  }

  // ============ FORMATO 'docentes' (original) ============
  const cardBg = c.color_tarjeta || '#1B0B30';
  const btnBg = c.color_boton || '#7c3aed';
  const modos = proyecto?.config_json?.modos_habilitados || {};
  const ingresar = (curso) => go(curso.id ? `/mentor/${curso.id}` : '/contenidos');

  return (
    <Wrapper $bg={buildBg(c, '#0b0218')} $padding={c.padding}>
      {(c.titulo || c.subtitulo) && (
        <Header>
          {c.titulo && <Title $size={c.tamano_titulo} dangerouslySetInnerHTML={{ __html: tl(c.titulo) }} />}
          {c.subtitulo && <Subtitle $size={c.tamano_subtitulo} dangerouslySetInnerHTML={{ __html: tl(c.subtitulo) }} />}
        </Header>
      )}
      <List>
        {documentos.map((curso, i) => {
          const foto = resolveImg(curso.docente_foto || curso.imagen);
          const flag = flagUrl(curso.docente_pais, 80);
          const pct = pctDoc(curso);
          const showMentor = Number(curso.modo_mentor) === 1 && modos.mentor !== false;
          const showEvalBase = Number(curso.modo_evaluacion) === 1 && modos.evaluacion !== false;
          const cursoListo = cursoVisto(curso.id, pct);
          const showEval = showEvalBase && (!c.eval_requiere_mentor || cursoListo);
          const evalPend = showEvalBase && !evalHechaDoc(curso) && (!c.eval_requiere_mentor || cursoListo);
          const glowEval = c.resaltar_eval_pendiente === true && showEval && !evalHechaDoc(curso);
          const legendaEval = c.resaltar_eval_pendiente === true && evalPend && cursoListo;
          const showReto = Number(curso.modo_reto) === 1 && modos.reto !== false;
          const showPractica = Number(curso.modo_practica) === 1 && modos.practica === true;
          const anyMode = showMentor || showEval || showReto || showPractica;
          // Varios docentes seleccionados desde el registro (docentes_ids → docentes_sel)
          const docsSel = Array.isArray(curso.docentes_sel) ? curso.docentes_sel : [];
          const footerEl = (
            <Footer>
              <Barra><BarraFill $pct={pct} $color={acento} /></Barra>
              <Pct $size={c.tamano_pct}>{pct}% COMPLETADO</Pct>
              {legendaEval && <PendienteEval $color={c.color_pendiente_eval || acento} $size={c.tamano_pct}>⚠ {tl(c.texto_pendiente_eval) || 'Pendiente evaluación'}</PendienteEval>}
              <BtnRow>
                {showMentor && <Btn $size={c.tamano_boton_ficha} $bg={btnBg} onClick={() => go(`/mentor/${curso.id}`)}>{tl(c.texto_boton) || 'Ingresar'}</Btn>}
                {showEval && <BtnAlt $size={c.tamano_boton_ficha} $color={acento} $glow={glowEval} onClick={() => go(`/evaluacion/${curso.id}`)}>{t('evaluacion')}</BtnAlt>}
                {showReto && <BtnAlt $size={c.tamano_boton_ficha} $color={acento} onClick={() => go(`/reto/${curso.id}`)}>{t('reto')}</BtnAlt>}
                {showPractica && <BtnAlt $size={c.tamano_boton_ficha} $color={acento} onClick={() => go(`/consulta/${curso.id}?modo=practica`)}>{t('practica')}</BtnAlt>}
                {!anyMode && <Btn $size={c.tamano_boton_ficha} $bg={btnBg} onClick={() => ingresar(curso)}>{tl(c.texto_boton) || 'Ingresar'}</Btn>}
              </BtnRow>
            </Footer>
          );

          if (docsSel.length > 0) {
            return (
              <Card key={i} $bg={cardBg}>
                <Body>
                  <DocentesLista>
                    {docsSel.map((d, di) => {
                      const dFoto = resolveImg(d.foto);
                      const dFlag = flagUrl(d.pais, 80);
                      return (
                        <DocenteItem key={di}>
                          <MiniFotoWrap>
                            <MiniFoto $src={dFoto} $size={c.tamano_foto} />
                            {dFlag && <MiniFlag src={dFlag} alt={d.pais || ''} onError={(e) => { e.target.style.display = 'none'; }} />}
                          </MiniFotoWrap>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Nombre $size={c.tamano_nombre} $color={acento}>{d.nombre}</Nombre>
                            {d.descripcion && <Texto $size={c.tamano_texto} dangerouslySetInnerHTML={{ __html: d.descripcion }} />}
                          </div>
                        </DocenteItem>
                      );
                    })}
                  </DocentesLista>
                  {footerEl}
                </Body>
              </Card>
            );
          }

          return (
            <Card key={i} $bg={cardBg}>
              <FotoWrap>
                <Foto $src={foto} />
                {flag && <Flag src={flag} alt={curso.docente_pais} onError={(e) => { e.target.style.display = 'none'; }} />}
              </FotoWrap>
              <Body>
                {curso.docente && <Nombre $size={c.tamano_nombre} $color={acento}>{curso.docente}</Nombre>}
                {curso.descripcion && <Texto $size={c.tamano_texto}>{tl(curso.descripcion)}</Texto>}
                {curso.ponencia && (
                  <>
                    <PonenciaLabel $size={c.tamano_ponencia_label} $color={acento}>{t('ponencia_label')}</PonenciaLabel>
                    <Texto $size={c.tamano_texto}>{tl(curso.ponencia)}</Texto>
                  </>
                )}
                {footerEl}
              </Body>
            </Card>
          );
        })}
      </List>
    </Wrapper>
  );
};

export default CursosFichasSection;
